import {Autocomplete, Button, CircularProgress, IconButton, Stack, TextField, Tooltip, useTheme} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {FiTrash2} from "react-icons/fi";
import {tenantProductsColumns} from "../../constants/dataGridColumns";
import ListView from "../ListView/ListView";
import useApiRequest from "../../hooks/useApiRequest";
import usePaginationSearch from "../../hooks/usePaginationSearch";
import useConfirmDialog from "../../hooks/useConfirmDialog";
import ConfirmDialog from "../ui/ConfirmDialog";
import {snackbar} from "../../utils/snackbar";
import TenantProductsFilterBar from "./TenantProductsFilterBar";
import {useAuthPermissions, ACTIONS, RESOURCES} from "../../hooks/useAuthPermissions";

const SDELSOL_PRODUCT_NAME = "Web SDELSOL";

const normalizeName = (value) => (value || "").trim().toLowerCase();

const TenantProductsGrid = ({tenantId, tenantNif, canAssign = true, canDelete = true}) => {
  const theme = useTheme();
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [internalCode, setInternalCode] = useState("");
  const [productsSelect, setProductsSelect] = useState([]);
  const [sdelsolCode, setSdelsolCode] = useState("");

  const {loading, list, makeRequest, create} = useApiRequest({
    baseUrl: `/tenants/${tenantId}/products`,
    resource: "",
    field: ""
  });

  const {list: listSdelsol} = useApiRequest({
    baseUrl: `/tenants/${tenantId}/products`,
    resource: "",
    field: ""
  });

  const {loading: syncing, makeRequest: syncRequest} = useApiRequest({
    baseUrl: `/tenants-products`,
    resource: "",
    field: ""
  });

  const {can} = useAuthPermissions();
  const canReadProducts = can(ACTIONS.READ, RESOURCES.PRODUCTS);

  const {
    data: dataProducts,
    loading: loadingProducts,
    list: listProducts
  } = useApiRequest({baseUrl: `/products`, resource: "products"});

  const fetcher = useCallback(
    async (params) => {
      const res = await list(params);
      if (!res?.success) return null;
      return {data: res.data};
    },
    [list]
  );

  const {applyFilters, resetFilters, dataGridProps, refresh} = usePaginationSearch({fetcher});

  const loadSdelsolCode = useCallback(async () => {
    const res = await listSdelsol({product_name: SDELSOL_PRODUCT_NAME}, {silentError: true});
    if (!res?.success) {
      setSdelsolCode("");
      return;
    }
    const rows = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
    // El filtro del backend puede ser parcial, comparamos el nombre exacto
    const found = rows.find((p) => normalizeName(p.product_name) === normalizeName(SDELSOL_PRODUCT_NAME));
    setSdelsolCode(found?.internal_code || "");
  }, [listSdelsol]);

  const refreshAll = useCallback(() => {
    refresh();
    if (canAssign && canReadProducts) listProducts({}, {silentError: true});
    loadSdelsolCode();
  }, [refresh, listProducts, loadSdelsolCode, canAssign, canReadProducts]);

  useEffect(() => {
    if (tenantId) {
      if (canAssign && canReadProducts) listProducts({}, {silentError: true});
      loadSdelsolCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, canAssign, canReadProducts]);

  const allProducts = useMemo(
    () => (Array.isArray(dataProducts?.data) ? dataProducts.data : Array.isArray(dataProducts) ? dataProducts : []),
    [dataProducts]
  );

  useEffect(() => {
    const assignedNames = (dataGridProps.rows || []).map((p) => p.product_name);
    const available = allProducts.filter((p) => !assignedNames.includes(p.name));
    setProductsSelect(available.map((p) => ({id: p.id, label: p.name})));
  }, [allProducts, dataGridProps.rows]);

  const {confirmOpen, itemName, openDialog, handleConfirm, handleCancel} = useConfirmDialog(async (id) => {
    const found = (dataGridProps.rows || []).find((p) => (p.product_name || p.id) === id);
    const code = found?.internal_code || "";
    const productId = found?.product_id || found?.id;

    const {success, error} = await makeRequest(
      "delete",
      `/${productId}/internal_code/${encodeURIComponent(code)}`,
      null,
      {silentError: true}
    );
    if (!success) {
      if (error?.code === "FORBIDDEN") {
        snackbar.error("No tienes permisos suficientes para eliminar este producto del tenant.");
      } else {
        snackbar.error(error?.userMessage);
      }
      return;
    }

    snackbar.success("Producto eliminado del tenant correctamente.");
    refreshAll();
  });

  const handleAccept = async () => {
    if (!selectedProductId || !internalCode.trim()) return;

    const {success, error} = await create(
      {
        product_id: selectedProductId,
        internal_code: internalCode.trim()
      },
      {silentError: true}
    );
    if (!success) {
      snackbar.error(error?.userMessage);
      return;
    }

    snackbar.success("Producto asignado al tenant correctamente.");
    setSelectedProductId(null);
    setInternalCode("");
    setIsAddingProduct(false);
    refreshAll();
  };

  const handleImportProducts = async () => {
    if (!tenantNif || !sdelsolCode) return;

    const {success, error} = await syncRequest(
      "post",
      "/sync",
      {
        nif: tenantNif,
        internal_code: sdelsolCode
      },
      {silentError: true}
    );
    if (!success) {
      if (error?.code === "FORBIDDEN") {
        snackbar.error("No tienes permisos suficientes para importar productos.");
      } else {
        snackbar.error(error?.userMessage || "No se han podido importar los productos del tenant.");
      }
      return;
    }

    snackbar.success("Productos importados correctamente.");
    refreshAll();
  };

  const importDisabledReason = !tenantNif
    ? "El tenant no tiene NIF."
    : !sdelsolCode
      ? `El tenant no tiene el producto ${SDELSOL_PRODUCT_NAME} con código interno; asígnalo primero.`
      : syncing
        ? "Importando productos…"
        : "";

  const handleOpenDeleteDialog = (id) => {
    const found = (dataGridProps.rows || []).find((p) => (p.product_name || p.id) === id);
    openDialog(id, found?.product_name || found?.name || "");
  };

  const columns = [
    ...tenantProductsColumns,
    ...(canDelete
      ? [
          {
            field: "actions",
            headerName: "Acciones",
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <IconButton
                aria-label="delete"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDeleteDialog(params.id);
                }}
              >
                <FiTrash2 />
              </IconButton>
            )
          }
        ]
      : [])
  ];

  return (
    <>
      <TenantProductsFilterBar onSearch={applyFilters} onReset={resetFilters} />
      <ListView
        columns={columns}
        getRowId={(row) => row.product_name || row.id}
        loading={loading || loadingProducts}
        dataGridProps={dataGridProps}
        topRightButtons={
          canAssign ? (
            <Stack direction="row" spacing={2}>
              {isAddingProduct && (
                <Stack direction="row" spacing={2}>
                  <Autocomplete
                    disablePortal
                    options={productsSelect}
                    sx={{width: 300}}
                    componentsProps={{
                      paper: {
                        sx: {
                          backgroundColor: theme.palette.colors.background,
                          color: theme.palette.text.primary
                        }
                      }
                    }}
                    onChange={(_, value) => setSelectedProductId(value?.id || null)}
                    renderInput={(params) => <TextField {...params} label="Producto" />}
                  />
                  <TextField
                    label="Código interno"
                    value={internalCode}
                    onChange={(e) => setInternalCode(e.target.value)}
                  />
                  <Button variant="contained" onClick={handleAccept}>
                    Aceptar
                  </Button>
                </Stack>
              )}
              {!isAddingProduct && (
                <Tooltip title={importDisabledReason}>
                  <span>
                    <Button
                      variant="outlined"
                      disabled={!tenantNif || !sdelsolCode || syncing}
                      onClick={handleImportProducts}
                      startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                      Sincronizar productos
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button
                variant="contained"
                color={isAddingProduct ? "error" : "primary"}
                onClick={() => setIsAddingProduct(!isAddingProduct)}
              >
                {isAddingProduct ? "Cancelar" : "Asignar producto"}
              </Button>
            </Stack>
          ) : null
        }
      />
      <ConfirmDialog
        open={confirmOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        entityName="producto"
        entityLabel={itemName}
      />
    </>
  );
};

export default TenantProductsGrid;
